import { useCallback, useSyncExternalStore } from 'react';
import {
  DEFAULT_DEV_SETTINGS,
  readDevSettings,
  writeDevSettings,
  type DevSettings,
} from '@/lib/dev-settings';

function subscribe(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('ienone:dev-settings-changed', callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener('ienone:dev-settings-changed', callback);
    window.removeEventListener('storage', callback);
  };
}

let cached: DevSettings = readDevSettings();
let cachedJson = JSON.stringify(cached);
function getSnapshot(): DevSettings {
  if (typeof window === 'undefined') return cached;
  const fresh = readDevSettings();
  const freshJson = JSON.stringify(fresh);
  if (freshJson !== cachedJson) {
    cached = fresh;
    cachedJson = freshJson;
  }
  return cached;
}

function getServerSnapshot(): DevSettings {
  return DEFAULT_DEV_SETTINGS;
}

export function useDevSettings(): {
  settings: DevSettings;
  update: (patch: Partial<DevSettings>) => void;
  reset: () => void;
} {
  const settings = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const update = useCallback((patch: Partial<DevSettings>) => {
    writeDevSettings({ ...readDevSettings(), ...patch });
  }, []);
  const reset = useCallback(() => writeDevSettings(DEFAULT_DEV_SETTINGS), []);
  return { settings, update, reset };
}
